import { Check } from "lucide-react";
import { cn } from "@/lib/utils";
import type { Workspace } from "@/hooks/useWorkspace";

type Props = {
  workspaces: Workspace[];
  /** Workspace IDs the item is currently visible in. */
  value: string[];
  onChange: (next: string[]) => void;
  /** ID of the owning workspace; cannot be unselected. */
  owningWorkspaceId: string;
  disabled?: boolean;
};

const labelFor = (w: Workspace) => w.workspace_label || w.display_name || "Workspace";

/**
 * Multi-select workspace picker used in the library item editor when
 * the active library mode is "shared". The owning workspace is shown
 * but locked so it can never be removed (the source has to live
 * somewhere editable).
 */
export const WorkspaceMembershipPicker = ({
  workspaces,
  value,
  onChange,
  owningWorkspaceId,
  disabled = false,
}: Props) => {
  if (workspaces.length === 0) return null;

  const toggle = (id: string) => {
    if (id === owningWorkspaceId) return;
    if (value.includes(id)) {
      onChange(value.filter((entry) => entry !== id));
    } else {
      onChange([...value, id]);
    }
  };

  return (
    <div className="flex flex-wrap gap-2">
      {workspaces.map((w) => {
        const checked = value.includes(w.id) || w.id === owningWorkspaceId;
        const isOwner = w.id === owningWorkspaceId;
        return (
          <button
            key={w.id}
            type="button"
            disabled={disabled || isOwner}
            onClick={() => toggle(w.id)}
            className={cn(
              "inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs font-medium transition-colors",
              checked
                ? "border-primary/40 bg-primary/10 text-foreground"
                : "border-border bg-background text-muted-foreground hover:bg-muted/50",
              (disabled || isOwner) && "cursor-not-allowed opacity-70",
            )}
            aria-pressed={checked}
          >
            {checked ? <Check className="h-3 w-3 text-primary" /> : null}
            {labelFor(w)}
            {isOwner ? <span className="ml-1 text-[10px] uppercase tracking-wide text-muted-foreground">owner</span> : null}
          </button>
        );
      })}
    </div>
  );
};
